import classnames from 'classnames'
import * as React from 'react'

import { IModalConfigInput } from '../../../../types/shared'
import BaseChart from '../../../charts/base_chart'
import {
  Button,
  ButtonColorPicker,
  Icon,
  InnerState,
  Label,
  Select,
  SelectOption,
  TooltipInput,
} from '../../../components'
import { OverlayType } from '../../../constants/common'
import Element from '../../../models/element'
import { overlaysByType } from '../../../overlays/overlays'
import { isForexFuturesCryptoPage } from '../../../utils/chart'
import { useForceUpdate } from '../../../utils/force-update'
import { useChartLayoutGlobalModel } from '../chart-layout-model'
import {
  handleAddOverlayElement,
  handleOverlayReplace,
  handleRemoveOverlayElement,
  handleUpdateOverlayAttributes,
} from './model-manipulation'

export interface IOverlayType {
  value: OverlayType
  label: string
}

export function OverlaysSelect() {
  const { forceUpdate } = useForceUpdate()
  const { chartLayoutModel } = useChartLayoutGlobalModel()
  const chartElementInstance = chartLayoutModel
    ?.charts()
    .first()
    ?.getAllElements()
    .find((element: Element) => element.isChart())?.instance as BaseChart
  const chartElementOverlays = chartElementInstance?.overlays
  const HIDDEN_OVERLAY_OPTIONS =
    chartElementOverlays
      ?.filter((item) => [OverlayType.Patterns, OverlayType.Vwap, OverlayType.Vp].includes(item.type))
      .map((item) => item.type) ?? []

  const DEFAULT_OVERLAYS = React.useMemo(() => {
    if (!chartLayoutModel) return []
    const ignoredOverlays = [OverlayType.Overlay, OverlayType.None, OverlayType.Ema2, OverlayType.Sma2]
    if (isForexFuturesCryptoPage(chartLayoutModel.specificChartFunctionality)) {
      ignoredOverlays.push(OverlayType.Patterns, OverlayType.Vwap)
    }

    return Object.keys(overlaysByType)
      .filter((item) => !ignoredOverlays.includes(item as OverlayType))
      .map((item) => ({ value: item as OverlayType, label: overlaysByType[item as OverlayType].label }))
  }, [chartLayoutModel])

  const onOverlaySelect = React.useCallback(
    ({ overlayType, overlayIndex }: { overlayType: OverlayType; overlayIndex: number }) => {
      if (chartLayoutModel) {
        const hasPatterns =
          chartElementInstance?.overlays.findIndex((overlay) => overlay.type === OverlayType.Patterns) !== -1
        const hasVWAP = chartElementInstance?.overlays.findIndex((overlay) => overlay.type === OverlayType.Vwap) !== -1

        if ((hasPatterns && overlayType === OverlayType.Patterns) || (hasVWAP && overlayType === OverlayType.Vwap)) {
          return
        }

        handleOverlayReplace({ overlayType, overlayIndex, chartLayoutModel })
        forceUpdate()
      }
    },
    [chartLayoutModel, forceUpdate, chartElementInstance?.overlays]
  )

  const onOverlayDelete = React.useCallback(
    (overlayIndex: number) => {
      if (chartLayoutModel) {
        handleRemoveOverlayElement({ overlayIndex, chartLayoutModel })
        forceUpdate()
      }
    },
    [chartLayoutModel, forceUpdate]
  )

  const onOverlayAdd = React.useCallback(() => {
    if (chartLayoutModel) {
      handleAddOverlayElement({ chartLayoutModel })
      forceUpdate()
    }
  }, [chartLayoutModel, forceUpdate])

  const onOverlayPeriodChange = ({
    value,
    overlayIndex,
    overlayInput,
  }: {
    value: string
    overlayIndex: number
    overlayInput: IModalConfigInput
  }) => {
    if (chartLayoutModel) {
      handleUpdateOverlayAttributes({
        chartLayoutModel,
        overlayIndex,
        attrs: { [overlayInput.name]: Number(value) },
      })
    }
  }

  const onOverlayColorChange = ({
    color,
    overlayIndex,
    overlayInput,
  }: {
    color: IModalConfigInput[]
    overlayIndex: number
    overlayInput: IModalConfigInput[]
  }) => {
    if (chartLayoutModel) {
      handleUpdateOverlayAttributes({
        chartLayoutModel,
        overlayIndex,
        attrs: { [overlayInput[0].name]: color[0].color },
      })
    }
  }

  const getParsedInputs = (inputsArray: IModalConfigInput[]) => {
    const otherInputs: IModalConfigInput[] = []
    const colorInputs: IModalConfigInput[] = []

    inputsArray?.forEach((input) => {
      if (input.type === 'color') {
        colorInputs.push(input)
      } else {
        otherInputs.push(input)
      }
    })

    return {
      otherInputs,
      colorInputs: colorInputs.map((input) => ({ ...input, color: input.value as string })),
    }
  }

  if (!chartLayoutModel) return null

  return (
    <>
      <Label title="Overlays" />
      <div className="mb-2 space-y-2">
        {chartElementOverlays?.map((overlay, overlayIndex) => {
          const overlayConfig = overlay.getModalConfig()
          const { inputsErrorMessages } = overlayConfig
          const { otherInputs, colorInputs } = getParsedInputs(overlayConfig.inputs)

          return (
            <div
              className="grid grid-cols-[1.25fr_1fr] gap-1 xs:grid-cols-[0.6fr_1fr]"
              key={`${overlay.type}-${overlayIndex}`}
            >
              <Select
                hasPopoverViewportHeightLimit
                aria-label={overlay.label}
                data-testid={`overlay-${overlayIndex}-select`}
                items={DEFAULT_OVERLAYS}
                placeholder="Select overlay"
                labelClassName="overflow-hidden -m-0.5 p-0.5"
                listProps={{ popoverMaxHeight: 415 }}
                size="medium"
                value={overlay.type}
                onChange={(item: IOverlayType) => {
                  onOverlaySelect({ overlayType: item.value, overlayIndex })
                }}
                hasPopoverButtonWidthLimit={false}
              >
                {DEFAULT_OVERLAYS.map((item) => (
                  <SelectOption
                    className={
                      HIDDEN_OVERLAY_OPTIONS.includes(item.value) && overlay.type !== item.value ? 'hidden' : ''
                    }
                    data-testid={`overlay-${overlayIndex}-select-${item.value}`}
                    key={item.value}
                  >
                    {item.label}
                  </SelectOption>
                ))}
              </Select>
              <div className="flex space-x-1">
                {otherInputs.map((overlayInput, confIndex) => (
                  <InnerState
                    {...overlayInput}
                    aria-label={overlayInput.label}
                    as={TooltipInput}
                    className="w-full min-w-20 max-w-24"
                    inputClass={classnames({ 'text-right': overlayInput.type === 'number' })}
                    data-testid={`overlay-${overlayIndex}-input-${confIndex}`}
                    error={!overlay.getIsValid(overlayInput.name)}
                    key={`${confIndex}-${overlay.type}-${overlayIndex}`}
                    label={undefined}
                    size="medium"
                    valueAccessor={(e: React.ChangeEvent<HTMLInputElement>) => e.target.value}
                    onBlur={forceUpdate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onOverlayPeriodChange({ value: e.target.value, overlayIndex, overlayInput })
                    }}
                    onInvalid={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
                      e.target.focus()
                    }}
                    tooltipContent={
                      <div>
                        <div>{overlayInput.label}</div>
                        {!overlay.getIsValid(overlayInput.name) && (
                          <div className="text-red-300 dark:text-red-500">{inputsErrorMessages[overlayInput.name]}</div>
                        )}
                      </div>
                    }
                  />
                ))}
                {colorInputs.length === 1 && (
                  <InnerState
                    as={ButtonColorPicker}
                    canSelectAlpha
                    canSelectCustom
                    colors={colorInputs}
                    data-testid={`overlay-${overlayIndex}-color-picker`}
                    buttonPickerDataTestId={`overlay-${overlayIndex}-color-picker-btn`}
                    onOverlayClick={false}
                    size="medium"
                    valueKey="colors"
                    onChange={(color: IModalConfigInput[]) =>
                      onOverlayColorChange({ color, overlayIndex, overlayInput: colorInputs })
                    }
                  />
                )}
                <Button
                  appearance="square"
                  data-testid={`overlay-${overlayIndex}-delete`}
                  size="medium"
                  type="button"
                  onClick={() => onOverlayDelete(overlayIndex)}
                >
                  <Icon name="trashCan" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      {chartElementInstance && chartElementInstance.overlays.length < 10 && (
        <Button leftContent="add" size="medium" type="button" onClick={onOverlayAdd} data-testid="add-new-overlay-btn">
          Overlays
        </Button>
      )}
    </>
  )
}
